/* eslint-disable max-len */
/* eslint-disable quote-props */
import { Box, Divider, Typography } from "@mui/material";
import React, { ComponentType, ReactElement } from "react";

import { CircleAlertIcon, CircleCheckIcon } from "../../../components/Icons";
import { IPrescriptionResponse } from "../../../services/prescription/prescription.interface";
import StatusChip from "../StoreContainer/components/StatusChip/StatusChip";
import { EPrescriptionStatus } from "../../../services/prescriptions/prescriptions.interface";
import PrescriptionActionButtons from "../StoreContainer/PrescriptionsContent/components/PrescriptionCard/PrescriptionActionButtons/PrescriptionActionButtons";

type IPrescriptionCardProps = {
  prescription: IPrescriptionResponse;
};

type PrescriptionState = "Expired" | "Active";

interface IStatusTheme {
  badgeColor: string;
  color: string;
}

interface IIconProps {
  width?: number;
  height?: number;
  color?: string;
}

const STATUS_THEME: Record<PrescriptionState, IStatusTheme> = {
  Expired: { badgeColor: "#F7E3DC", color: "#D35633" },
  Active: { badgeColor: "#D0E7D7", color: "#537561" },
};

const STATUS_ICONS: Record<PrescriptionState, ComponentType<IIconProps>> = {
  Expired: CircleAlertIcon,
  Active: CircleCheckIcon,
};

const DEFAULT_THEME: IStatusTheme = { badgeColor: "#DAD7D6", color: "#262626" };

const getStatusIcon = (state: PrescriptionState, color: string): ReactElement => {
  const IconComponent = STATUS_ICONS[state] || CircleAlertIcon;
  return <IconComponent width={12} height={12} color={color} />;
};

const PrescriptionCard = ({ prescription }: IPrescriptionCardProps) => {
  const { id, indications, name, state, productUrl } = prescription;

  const tempState = prescription.state as PrescriptionState;
  const { badgeColor, color } = STATUS_THEME[tempState] || DEFAULT_THEME;
  const icon = getStatusIcon(tempState, color);

  return (
    <Box
      className="rounded-lg p-4 mb-3 bg-white"
      sx={{
        border: "1px solid",
        borderColor: "sand.main",
        boxShadow: "2px 2px 18px 0px rgba(55, 54, 23, 0.1)",
      }}
    >
      <Box className="flex justify-between items-center">
        <Typography fontSize={16} fontWeight={600}>
          {name}
        </Typography>
        <StatusChip label={prescription.state} badgeColor={badgeColor} color={color} icon={icon} />
      </Box>

      <Divider sx={{ my: 1.5, borderColor: "sand.main" }} />

      <Box className="flex flex-col gap-2">
        <Typography variant="body2" sx={{ color: "gray.dark" }}>
          Instructions:
        </Typography>
        <Typography variant="body1">{indications || "/"}</Typography>
      </Box>

      <Divider sx={{ mt: 1.5, borderColor: "gray.light" }} />

      <Box className="pt-3">
        <PrescriptionActionButtons
          status={state === "Expired" ? EPrescriptionStatus.EXPIRED : EPrescriptionStatus.ACTIVE}
          prescriptionId={id}
          itemId={id}
          productUrl={productUrl}
          size="small"
          prescription={prescription}
          source="care"
        />
      </Box>
    </Box>
  );
};

export default PrescriptionCard;
